import { useState, useEffect } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Globe, ArrowLeft, Plus, DollarSign, Clock, FileText } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";

const CountryDetailPage = () => {
  const { countryId } = useParams();
  const navigate = useNavigate();
  const [country, setCountry] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    loadCountry();
  }, [countryId]);

  const loadCountry = async () => {
    try {
      const data = await api.getCountry(countryId);
      setCountry(data);
    } catch (error) {
      toast.error("Country not found");
      navigate("/dashboard");
    } finally {
      setIsLoading(false);
    }
  };

  const handleAddVisa = async (visaId) => {
    try {
      await api.addUserCountry({
        country_id: countryId,
        visa_id: visaId
      });
      toast.success("Added to your dashboard!");
    } catch (error) {
      if (error.message.includes("Already tracking")) {
        toast.info("Already tracking this visa");
      } else {
        toast.error("Failed to add");
      }
    }
  };

  const getDifficultyColor = (difficulty) => {
    switch (difficulty) {
      case "low": return "bg-green-100 text-green-800";
      case "medium": return "bg-yellow-100 text-yellow-800";
      case "high": return "bg-red-100 text-red-800";
      default: return "bg-gray-100 text-gray-800";
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="w-10 h-10 border-4 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!country) return null;

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="sticky top-0 z-50 glass border-b border-border/50">
        <div className="max-w-7xl mx-auto px-4 md:px-8 h-16 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button variant="ghost" size="icon" onClick={() => navigate(-1)}>
              <ArrowLeft className="h-5 w-5" />
            </Button>
            <div className="flex items-center gap-2">
              <Globe className="h-6 w-6 text-primary" />
              <span className="font-bold text-lg font-['Manrope']">Nomad Roadmap</span>
            </div>
          </div>
          <Link to="/dashboard">
            <Button variant="outline" size="sm">Dashboard</Button>
          </Link>
        </div>
      </header>

      {/* Hero */}
      <div className="relative h-64 md:h-80 overflow-hidden">
        {country.image_url ? (
          <img 
            src={country.image_url} 
            alt={country.name}
            className="w-full h-full object-cover"
          />
        ) : (
          <div className="w-full h-full bg-gradient-to-r from-primary/20 to-accent/20" />
        )}
        <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/30 to-transparent" />
        <div className="absolute bottom-0 left-0 right-0 p-6 md:p-8">
          <div className="max-w-7xl mx-auto">
            <div className="flex items-center gap-4">
              <span className="text-5xl">{country.flag_emoji}</span>
              <div>
                <h1 className="text-3xl md:text-4xl font-bold text-white font-['Manrope']">{country.name}</h1>
                <p className="text-white/80">{country.region}</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      <main className="max-w-7xl mx-auto px-4 md:px-8 py-8">
        {/* Visa Types */}
        <div className="mb-8">
          <h2 className="text-xl font-semibold font-['Manrope'] mb-4">
            Available Visa Options ({country.visas?.length || 0})
          </h2>
          
          <div className="grid gap-4">
            {country.visas?.map((visa, index) => (
              <Card 
                key={visa.visa_id}
                className="border-border/50 hover:shadow-md transition-all animate-fade-in"
                style={{ animationDelay: `${index * 0.05}s` }}
              >
                <CardContent className="p-6">
                  <div className="flex flex-col md:flex-row md:items-center gap-4">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-2">
                        <h3 className="font-semibold text-lg">{visa.name}</h3>
                        <Badge className={getDifficultyColor(visa.difficulty)}>
                          {visa.difficulty}
                        </Badge>
                      </div>
                      <p className="text-muted-foreground text-sm mb-4">{visa.description}</p>
                      
                      <div className="flex flex-wrap gap-4 text-sm">
                        {visa.income_requirement && (
                          <div className="flex items-center gap-1 text-muted-foreground">
                            <DollarSign className="h-4 w-4" />
                            ${visa.income_requirement.toLocaleString()}/mo min
                          </div>
                        )}
                        {visa.savings_requirement && (
                          <div className="flex items-center gap-1 text-muted-foreground">
                            <DollarSign className="h-4 w-4" />
                            ${visa.savings_requirement.toLocaleString()} savings
                          </div>
                        )}
                        <div className="flex items-center gap-1 text-muted-foreground">
                          <Clock className="h-4 w-4" />
                          {visa.processing_time}
                        </div>
                        <div className="flex items-center gap-1 text-muted-foreground">
                          <FileText className="h-4 w-4" />
                          {visa.required_documents?.length || 0} documents
                        </div>
                      </div>
                    </div>

                    <div className="flex gap-2">
                      <Button 
                        variant="outline"
                        onClick={() => navigate(`/visa/${countryId}/${visa.type}`)}
                        data-testid={`view-visa-${visa.type}`}
                      >
                        View Details
                      </Button>
                      <Button 
                        onClick={() => handleAddVisa(visa.visa_id)}
                        className="bg-primary hover:bg-primary/90"
                        data-testid={`add-visa-${visa.type}`}
                      >
                        <Plus className="h-4 w-4 mr-1" />
                        Track
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>

        {/* Disclaimer */}
        <div className="bg-secondary/50 rounded-xl p-6 text-center">
          <p className="text-sm text-muted-foreground">
            <strong>Note:</strong> Requirements are subject to change. Always verify with official government sources.
          </p>
        </div>
      </main>
    </div>
  );
};

export default CountryDetailPage;
